<template>
  <SettingsPaneV2
    class="labels-pane"
    header="Dataset Labels"
  >
    <template
      v-if="!datasetLabelsStore.shouldShowInitialView"
      #subheader
    >
      Use labels to organize datasets across projects and use cases
    </template>
    <template #header-action>
      <DarwinButton
        size="md"
        kind="primary"
        flair="rounded"
        @click="showCreateLabelForm = true"
      >
        Add Label
      </DarwinButton>
    </template>
    <template #body>
      <div
        v-if="datasetLabelsStore.isLoading"
        class="labels-pane__spinner"
      >
        <Spinner
          size="md"
          color="dark"
          label="Loading labels..."
        />
      </div>
      <LabelsInitialView
        v-else-if="datasetLabelsStore.shouldShowInitialView"
        @click:setup-labels="handleSetupLabels"
      />
      <LabelsTable
        v-else
        :labels="datasetLabelsStore.labels"
        :show-create-label-form="showCreateLabelForm"
        @close-create-form="showCreateLabelForm = false"
        @create-label="handleCreateLabel"
        @update-label="handleUpdateLabel"
        @delete-label="handleDeleteLabel"
      />
    </template>
  </SettingsPaneV2>
</template>

<script setup lang="ts">
import { onMounted, ref, nextTick } from 'vue'

import DarwinButton from '@/uiKit/Button/DarwinButton.vue'
import SettingsPaneV2 from '@/layouts/Main/SettingsDialog/Panes/SettingsPaneV2.vue'
import LabelsInitialView from './LabelsInitialView.vue'
import LabelsTable from './LabelsTable.vue'
import Spinner from '@/uiKit/Spinner/Spinner.vue'
// eslint-disable-next-line boundaries/element-types
import { useDatasetLabelsStore } from '@/modules/Datasets/useDatasetLabelsStore'
// eslint-disable-next-line boundaries/element-types
import { getRandomPresetColor } from '@/modules/Datasets/datasetLabelUtils'
import type { DatasetLabelPayload } from '@/store/types/DatasetLabelPayload'

const datasetLabelsStore = useDatasetLabelsStore()
const showCreateLabelForm = ref(false)

const handleCreateLabel = async (name: string): Promise<void> => {
  const { bgColor } = getRandomPresetColor()

  await datasetLabelsStore.createLabel(name, bgColor, [])
  showCreateLabelForm.value = false
}

const handleSetupLabels = (): void => {
  datasetLabelsStore.initializeLabels()
  nextTick(() => {
    showCreateLabelForm.value = true
  })
}

const handleUpdateLabel = (
  label: DatasetLabelPayload,
  updatedData: { name: string; color: string },
): void => {
  datasetLabelsStore.updateLabel(label.id, {
    ...updatedData,
    dataset_ids: label.dataset_ids,
  })
}

const handleDeleteLabel = (label: DatasetLabelPayload): void => {
  datasetLabelsStore.deleteLabel(label)
}

onMounted(async () => {
  await datasetLabelsStore.loadLabels()
})
</script>

<style lang="scss" scoped>
@import '@/uiKit/assets/spacings.scss';

.labels-pane {
  :deep(.settings-pane__container) {
    height: 100%;
    max-width: 100%;
  }

  :deep(.settings-pane__body) {
    padding: 0;
  }

  &__spinner {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 100%;
    min-height: 200px;
  }
}
</style>
